import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Modal,
  TextField,
  Typography,
  useTheme
} from "@mui/material";

import { postNotification } from "../../../components/utilities/Notifications";

import * as Yup from "yup";
import { Formik } from "formik";
import ListResponsive from "../../../components/other/ListResponsive";
import { handlePagingAll } from "../../../components/api/APIUtilities";
import StyledMenu from "../../../components/layout/StyledMenu";
import ExportModal from "../../../components/modals/ExportModal";
import {
  BCPAngleDown,
  BCPSquarePlus,
  BCPXmark
} from "../../../components/icons/bcp/BCPIcons";
import { postData } from "../../../components/utilities/UtilityFunctions";
import ModalLoadingMessage from "../../../components/modals/ModalLoadingMessage";

export default function EventAccessCodes(props) {
  const { event, authContext } = props;

  const [processing, setProcessing] = useState(false);
  const [subLoading, setSubLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const [accessCodes, setAccessCodes] = useState(null);
  const [generateAccessCodesOpen, setGenerateAccessCodesOpen] = useState(false);

  const refreshAccessCodes = async () => {
    try {
      setSubLoading(true);
      var apiAccessCodes = await handlePagingAll(
        `/events/${event.id}/accessCodes`,
        {
          "expand[]": ["player", "teamPlayer"]
        }
      );

      setAccessCodes(apiAccessCodes);
    } catch (err) {
      postNotification("error", err);
    }

    setSubLoading(false);
  };

  useEffect(() => {
    refreshAccessCodes();
    // eslint-disable-next-line
  }, []);

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2
          }}
        >
          <Typography variant="h3">{event.name}</Typography>

          <Typography variant="h4">Access Codes</Typography>
        </Grid>

        <Grid
          item
          xs={12}
          sx={{
            p: 2
          }}
        >
          <ListResponsive
            originalList={subLoading ? null : accessCodes}
            doSearch
            doPages
            initialRowsPerPage={25}
            actionButton={[
              {
                props: {
                  variant: "contained",
                  color: "primary",
                  onClick: (e) => {
                    setGenerateAccessCodesOpen(true);
                  },
                  startIcon: <BCPSquarePlus />
                },
                label: "Generate Access Codes"
              },
              {
                props: {
                  variant: "outlined",
                  color: "primary",
                  "aria-expanded": Boolean(optionsAnchorEl) ? true : undefined,
                  onClick: (e) => {
                    setOptionsAnchorEl(e.currentTarget);
                  },
                  endIcon: <BCPAngleDown />
                },
                label: "Options"
              }
            ]}
            searchProps={{
              label: "Search Access Codes",
              performSearch: (originalList, searchText) => {
                if (!searchText || searchText === "") {
                  return originalList;
                } else {
                  var filteredList = originalList.filter(function (el) {
                    if (el?.code?.toLowerCase().includes(searchText))
                      return true;
                    if (
                      el?.player?.firstName?.toLowerCase().includes(searchText)
                    )
                      return true;
                    if (
                      el?.player?.lastName?.toLowerCase().includes(searchText)
                    )
                      return true;
                    if (el?.player?.email?.toLowerCase().includes(searchText))
                      return true;

                    return false;
                  });
                  return filteredList;
                }
              }
            }}
            rowProps={{
              headings: <ListRow heading />,
              mapFunction: (row, index) => <ListRow key={index} row={row} />
            }}
          />
        </Grid>
      </Grid>
      <GenerateAccessCodesModal
        event={event}
        refreshAccessCodes={refreshAccessCodes}
        accessCodes={accessCodes}
        setProcessing={setProcessing}
        setMessage={setMessage}
        open={generateAccessCodesOpen}
        setOpen={setGenerateAccessCodesOpen}
      />

      <OptionsMenu
        anchorEl={optionsAnchorEl}
        setAnchorEl={setOptionsAnchorEl}
        setLoading={setProcessing}
        event={event}
        authContext={authContext}
      />

      {Boolean(processing) && (
        <ModalLoadingMessage message={message} open={processing} />
      )}
    </Container>
  );
}

function ListRow(props) {
  const { heading, row } = props;
  return (
    <Grid
      item
      xs={12}
      sx={{
        p: 1,
        mb: 1,
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: heading ? "4px 4px 0 0" : 1,
        boxShadow: 1,
        backgroundColor: heading ? "table.headerBackground" : null,
        color: heading ? "table.headerColor" : null
      }}
    >
      <Grid container>
        <Grid item xs>
          <Typography>{heading ? "Code" : row.code}</Typography>
        </Grid>
        <Grid item xs>
          <Typography>
            {heading
              ? "Player"
              : row.player
                ? `${row?.player?.firstName || row?.player?.user?.firstName} ${
                    row?.player?.lastName || row?.player?.user?.lastName
                  }`
                : null}
          </Typography>
          {!heading && row?.player?.email && (
            <Typography>
              <Link href={`mailto:${row.player.email}`} target="_blank">
                {row.player.email}
              </Link>
            </Typography>
          )}
        </Grid>
        <Grid item xs textAlign="center" sx={{ maxWidth: "100px" }}>
          {heading ? (
            "Used"
          ) : (
            <Chip
              label={!row.active ? "USED" : "UNUSED"}
              size="small"
              sx={{ width: "100px" }}
              color={!row.active ? "success" : "warning"}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}

function GenerateAccessCodesModal(props) {
  const {
    event,
    accessCodes,
    refreshAccessCodes,
    setProcessing,
    setMessage,
    open,
    setOpen
  } = props;

  return (
    <Modal
      disableAutoFocus
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
      sx={{ zIndex: 9999 }}
    >
      {open ? (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setOpen(false);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
            }}
          >
            <Grid item xs>
              <Typography variant="h4" component="h2">
                Generate Access Codes
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              number:
                event?.numTickets &&
                accessCodes?.length &&
                event?.numTickets > accessCodes.length
                  ? event?.numTickets - accessCodes.length
                  : event?.numTickets < 5000
                    ? event?.numTickets
                    : 100
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              number: Yup.number()
                .min(1, "Minimum 1")
                .max(5000, "Maximum 5000")
                .required("Required")
            })}
            onSubmit={async (values) => {
              setProcessing(true);

              try {
                const numOfCodes = values.number;

                setProcessing(true);
                setMessage(`Generating ${numOfCodes} access codes...`);
                setOpen(false);

                for (let i = 0; i < numOfCodes; i += 100) {
                  const codesToGenerate = Math.min(100, numOfCodes - i);

                  await postData(`/events/${event?.id}/generateAccessCodes`, {
                    body: {
                      number: codesToGenerate
                    }
                  });
                }

                await refreshAccessCodes();
                postNotification(
                  "success",
                  `Successfully generated ${numOfCodes} access codes.`
                );

                return;
              } catch (err) {
                postNotification("error", err);
              } finally {
                setMessage(null);
                setProcessing(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      label="Number of Codes"
                      name="number"
                      value={values.number}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      required
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.number && errors.number)}
                      helperText={touched.number && errors.number}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12} sx={{ mb: 2 }}>
                    <Typography variant="body1">
                      Codes Generated:{" "}
                      <span style={{ fontWeight: "bold" }}>
                        {accessCodes?.length || 0}
                      </span>
                    </Typography>
                    <Typography variant="body1">
                      Number of Tickets:{" "}
                      <span style={{ fontWeight: "bold" }}>
                        {event?.numTickets || 0}
                      </span>
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      type="submit"
                    >
                      Generate
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      ) : (
        <></>
      )}
    </Modal>
  );
}

function OptionsMenu(props) {
  const { anchorEl, setAnchorEl, setLoading, event, authContext } = props;
  const [exportModal, setExportModal] = useState(false);
  const open = Boolean(anchorEl);
  const theme = useTheme();

  return (
    <>
      <StyledMenu
        id="options-menu"
        MenuListProps={{
          "aria-labelledby": "options-button"
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        sx={{
          "& .MuiPaper-root": {
            minWidth: ["auto", "250px !important"],
            border: `1px solid ${anchorEl ? theme?.palette?.divider : "transparent"}`
          }
        }}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={async (e) => {
            setExportModal(true);
            setAnchorEl(null);
          }}
          disableRipple
        >
          Export Access Codes
        </MenuItem>
      </StyledMenu>
      <ExportModal
        label="Export Access Codes"
        exportTypes={["accessCodes"]}
        event={event}
        setLoading={setLoading}
        open={exportModal}
        setOpen={setExportModal}
        authContext={authContext}
      />
    </>
  );
}
